<template>
  <section class="custom-wrapper">
    <div class="row mt-2 mb-3">
      <div class="col-12">
        <h5 class="mb-3">Training/Certifications</h5>
        <p class="text-muted mb-4">Select the training programs and certifications your technicians have completed.</p>
        
        <div v-if="groupedTrainingOptions && Object.keys(groupedTrainingOptions).length > 0" class="training-container">
          <div 
            v-for="(certifications, certificationType) in groupedTrainingOptions" 
            :key="certificationType" 
            class="certification-type-group mb-4"
          >
            <h6 class="certification-type-header">{{ certificationType }}</h6>
            <div class="certifications-list">
              <div 
                v-for="certification in certifications" 
                :key="certification.id" 
                class="certification-item mb-2"
              >
                <div class="form-check">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    :id="certification.id"
                    :value="certification.id"
                    v-model="selectedCertifications"
                    @change="changed"
                  >
                  <label class="form-check-label" :for="certification.id">
                    <strong>{{ certification.name }}</strong>
                    <span v-if="certification.description" class="text-muted ms-2">
                      - {{ certification.description }}
                    </span>
                  </label>
                </div>
                
                <!-- Upload/View Certificate Image - only show for selected certifications -->
                <div v-if="selectedCertifications.includes(certification.id)" class="certification-image-section mt-2">
                  <div v-if="getCertificationImage(certification.id)" class="uploaded-image-preview">
                    <img 
                      :src="getCertificationImage(certification.id).url" 
                      :alt="`${certification.name} Certificate`"
                      class="certification-thumbnail"
                      @click="previewCertificationImage(certification.id)"
                    >
                    <div class="image-actions">
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary me-1" 
                        @click="openImageUploadModal(certification)"
                      >
                        <i class="fas fa-edit"></i> Replace
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-danger" 
                        @click="removeCertificationImage(certification.id)"
                      >
                        <i class="fas fa-trash"></i> Remove
                      </button>
                    </div>
                  </div>
                  <div v-else>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-success" 
                      @click="openImageUploadModal(certification)"
                    >
                      <i class="fas fa-upload"></i> Upload Certificate Image
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="alert alert-info">
          <i class="fas fa-info-circle me-2"></i>
          Loading training and certification options...
        </div>
        
        <div v-if="selectedCertifications.length === 0 && Object.keys(groupedTrainingOptions).length > 0" class="alert alert-info mt-3">
          <i class="fas fa-info-circle me-2"></i>
          Please select the training programs and certifications your technicians have completed.
        </div>
        
        <div v-else-if="selectedCertifications.length > 0" class="alert alert-success mt-3">
          <i class="fas fa-check-circle me-2"></i>
          You have selected {{ selectedCertifications.length }} training/certification{{ selectedCertifications.length > 1 ? 's' : '' }}.
        </div>
      </div>
    </div>
  </section>
  
  <!-- Certificate Image Upload Modal -->
  <div v-if="uploadModal.show" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Upload Certificate Image</h5>
          <button type="button" class="btn-close" @click="closeUploadModal"></button>
        </div>
        <div class="modal-body">
          <h6>{{ uploadModal.certification?.name }}</h6>
          <p class="text-muted">Upload an image of your certificate or diploma for this training/certification.</p>
          
          <div class="upload-area" @click="triggerFileInput" :class="{ 'uploading': isUploading }">
            <div v-if="!isUploading" class="upload-content">
              <i class="fas fa-cloud-upload-alt upload-icon"></i>
              <p>Click to select certificate image</p>
              <small class="text-muted">Supports JPG, PNG, GIF (max 10MB)</small>
            </div>
            <div v-else class="upload-progress">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Uploading...</span>
              </div>
              <p class="mt-2">Uploading certificate image...</p>
            </div>
          </div>
          
          <input 
            ref="certificationFileInput"
            type="file" 
            class="d-none" 
            accept="image/*"
            @change="handleCertificationFileSelection"
            :disabled="isUploading"
          >
          
          <div v-if="uploadError" class="alert alert-danger mt-3">
            <i class="fas fa-exclamation-circle me-2"></i>
            {{ uploadError }}
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeUploadModal" :disabled="isUploading">Cancel</button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Certificate Image Preview Modal -->
  <div v-if="previewModal.show" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Certificate Image</h5>
          <button type="button" class="btn-close" @click="closePreviewModal"></button>
        </div>
        <div class="modal-body text-center">
          <h6>{{ previewModal.certificationName }}</h6>
          <img :src="previewModal.imageUrl" :alt="previewModal.certificationName" class="img-fluid">
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closePreviewModal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { locationStore } from '@/stores/location'
import { authStore } from '@/stores/auth'
import axios from 'axios'

export default {
  name: 'TrainingCertifications',
  props: {
    firm: Object
  },
  emits: ['changed'],
  data() {
    return {
      isUploading: false,
      uploadError: null,
      uploadModal: {
        show: false,
        certification: null
      },
      previewModal: {
        show: false,
        certificationName: '',
        imageUrl: ''
      }
    }
  },
  computed: {
    ...mapState(authStore, ['token']),
    ...mapState(locationStore, ['profileData', 'trainingCertificationOptions', 'location']),
    launchpadFirmId() {
      return this.location?.firmId || this.launchpadFirmId
    },
    selectedCertifications: {
      get() {
        return this.profileData?.trainingCertificates || []
      },
      set(value) {
        this.updateProfileField('trainingCertificates', value, this.launchpadFirmId)
      }
    },
    groupedTrainingOptions() {
      if (!this.trainingCertificationOptions || this.trainingCertificationOptions.length === 0) {
        return {}
      }
      
      // Group certifications by certification type
      const grouped = {}
      this.trainingCertificationOptions.forEach(certification => {
        if (!grouped[certification.certificationType]) {
          grouped[certification.certificationType] = []
        }
        grouped[certification.certificationType].push(certification)
      })
      
      // Sort certifications within each group by display order, then name
      Object.keys(grouped).forEach(type => {
        grouped[type].sort((a, b) => {
          if (a.displayOrder !== b.displayOrder) {
            return a.displayOrder - b.displayOrder
          }
          return a.name.localeCompare(b.name)
        })
      })
      
      return grouped
    },
    apiUrl() {
      return window.config ? window.config.apiUrl : ''
    }
  },
  methods: {
    ...mapActions(locationStore, ['updateProfileField', 'saveProfileData']),
    changed() {
      this.$emit('changed')
    },
    async save() {
      if (this.launchpadFirmId) {
        await this.saveProfileData(this.launchpadFirmId)
      }
    },
    
    // Certification image management methods
    getCertificationImage(certificationId) {
      const images = this.profileData?.certificationImages || {}
      return images[certificationId] || null
    },
    
    openImageUploadModal(certification) {
      this.uploadModal.certification = certification
      this.uploadModal.show = true
      this.uploadError = null
    },
    
    closeUploadModal() {
      this.uploadModal.show = false
      this.uploadModal.certification = null
      this.uploadError = null
    },
    
    triggerFileInput() {
      if (this.isUploading) return
      this.$refs.certificationFileInput.click()
    },
    
    async handleCertificationFileSelection(event) {
      const file = event.target.files[0]
      if (!file) return
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.uploadError = 'Please select an image file (JPG, PNG, GIF, etc.)'
        return
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.uploadError = 'Image file must be smaller than 10MB'
        return
      }
      
      await this.uploadCertificationImage(file)
      event.target.value = '' // Clear the input
    },
    
    async uploadCertificationImage(file) {
      this.uploadError = null
      this.isUploading = true
      
      try {
        const formData = new FormData()
        formData.append('files', file, file.name)
        formData.append('folder', 'clientfiles')
        formData.append('firm', this.launchpadFirmId.toString())
        formData.append('id', this.launchpadFirmId.toString())
        formData.append('vin', '')
        formData.append('name', '')
        
        const response = await axios({
          method: 'post',
          url: `${this.apiUrl}/data/uploadFiles`,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${this.token}`
          }
        })
        
        // Get the URL from the response
        const imageUrl = response.data
        const certificationId = this.uploadModal.certification.id
        
        // Update the certification images in profile data
        const certificationImages = this.profileData?.certificationImages || {}
        certificationImages[certificationId] = {
          url: imageUrl,
          name: file.name,
          uploadedDate: new Date().toISOString()
        }
        
        this.updateProfileField('certificationImages', certificationImages, this.launchpadFirmId)
        
        // Save to backend immediately
        await this.saveProfileData(this.launchpadFirmId)
        
        // Close the modal and emit change
        this.closeUploadModal()
        this.$emit('changed')
        
      } catch (error) {
        console.error('Certificate image upload error:', error)
        this.uploadError = error.response?.data?.message || 'Failed to upload certificate image. Please try again.'
      } finally {
        this.isUploading = false
      }
    },
    
    previewCertificationImage(certificationId) {
      const image = this.getCertificationImage(certificationId)
      const certification = this.trainingCertificationOptions.find(c => c.id === certificationId)
      
      if (image && certification) {
        this.previewModal.certificationName = certification.name
        this.previewModal.imageUrl = image.url
        this.previewModal.show = true
      }
    },
    
    closePreviewModal() {
      this.previewModal.show = false
      this.previewModal.certificationName = ''
      this.previewModal.imageUrl = ''
    },
    
    async removeCertificationImage(certificationId) {
      if (!confirm('Are you sure you want to remove this certificate image?')) {
        return
      }
      
      try {
        const image = this.getCertificationImage(certificationId)
        if (image && image.name) {
          // Delete from Azure storage
          await axios.delete(`${this.apiUrl}/data/removeFile`, {
            params: {
              folder: 'clientfiles',
              filename: image.name,
              id: this.launchpadFirmId
            },
            headers: {
              Authorization: `Bearer ${this.token}`
            }
          })
        }
        
        // Remove from local data
        const certificationImages = { ...this.profileData?.certificationImages || {} }
        delete certificationImages[certificationId]
        
        this.updateProfileField('certificationImages', certificationImages, this.launchpadFirmId)
        
        // Save to backend immediately
        await this.saveProfileData(this.launchpadFirmId)
        
        this.$emit('changed')
        
      } catch (error) {
        console.error('Certificate image deletion error:', error)
        alert('Failed to remove certificate image. Please try again.')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-wrapper {
  width: 100%;
  position: relative;
  padding: 20px;
}

h5 {
  color: #495057;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.training-container {
  margin-top: 1rem;
}

.certification-type-group {
  border: 1px solid #dee2e6;
  border-radius: 0.75rem;
  padding: 1.5rem;
  background-color: #f8f9fa;
  margin-bottom: 1.5rem;
}

.certification-type-header {
  color: #495057;
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #dee2e6;
}

.certifications-list {
  margin-top: 0.5rem;
}

.certification-item {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.75rem;
  background-color: #ffffff;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    border-color: #80bdff;
    background-color: #e7f3ff;
  }
}

.form-check {
  margin-bottom: 0;
}

.form-check-input {
  margin-top: 0.125rem;
  
  &:checked {
    background-color: #0d6efd;
    border-color: #0d6efd;
  }
  
  &:focus {
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
}

.form-check-label {
  cursor: pointer;
  margin-left: 0.5rem;
  line-height: 1.4;
  
  strong {
    color: #495057;
    font-size: 1rem;
  }
  
  .text-muted {
    font-size: 0.9rem;
    color: #6c757d !important;
  }
}

.alert {
  border-radius: 0.5rem;
  
  i {
    font-size: 1rem;
  }
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.text-muted {
  color: #6c757d !important;
}

// Certification image upload styling
.certification-image-section {
  margin-left: 1.75rem;
  padding: 0.75rem;
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  margin-top: 0.5rem;
}

.uploaded-image-preview {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.certification-thumbnail {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #80bdff;
    transform: scale(1.05);
  }
}

.image-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

// Upload modal styling
.upload-area {
  border: 2px dashed #dee2e6;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #f8f9fa;
  
  &:hover:not(.uploading) {
    border-color: #80bdff;
    background-color: #e7f3ff;
  }
  
  &.uploading {
    cursor: not-allowed;
    opacity: 0.7;
  }
}

.upload-content {
  .upload-icon {
    font-size: 2.5rem;
    color: #6c757d;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #495057;
  }
  
  small {
    color: #6c757d;
  }
}

.upload-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  
  .spinner-border {
    color: #0d6efd;
  }
  
  p {
    color: #495057;
    margin-bottom: 0;
  }
}

// Modal improvements
.modal {
  z-index: 1055;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  opacity: 0.5;
  
  &:hover {
    opacity: 0.75;
  }
}
</style>